import { useEffect, useRef } from 'react';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

// Phase 3 WP5.1 — first Realtime slice. Subscribe to Postgres changes on a single
// `public` table and forward every INSERT/UPDATE/DELETE to a handler. RLS-filtered:
// Realtime evaluates the table's RLS policies against the subscriber's JWT and only
// delivers rows the user is allowed to SELECT (content: WP4 policies; audio_tracks:
// the staff policy added in wp5-realtime-publication.sql).
//
// WHY setAuth: postgres_changes runs the RLS check as the connected socket's role.
// Without the user's access token the socket is `anon`, so a CMS operator would never
// receive draft-content or staff-only audio_tracks changes. Supabase docs: "set the
// token after instantiating the Supabase client and before connecting to a Channel."
//
// The handler is kept in a ref so callers can pass an inline closure (e.g. one that
// reads the current filters) without re-subscribing on every render.

type ChangeHandler<T extends Record<string, unknown>> = (
  payload: RealtimePostgresChangesPayload<T>,
) => void;

let channelSeq = 0;

export function useRealtimeTable<T extends Record<string, unknown> = Record<string, unknown>>(
  table: string,
  onChange: ChangeHandler<T>,
  enabled = true,
): void {
  const handlerRef = useRef(onChange);
  handlerRef.current = onChange;

  useEffect(() => {
    if (!enabled) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    const topic = `cms:${table}:${++channelSeq}`;

    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      const token = data.session?.access_token;
      if (token) supabase.realtime.setAuth(token);

      channel = supabase
        .channel(topic)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table },
          (payload) => handlerRef.current(payload as RealtimePostgresChangesPayload<T>),
        )
        .subscribe();

      // If the component unmounted while getSession() / subscribe() was in flight, the
      // cleanup below already ran with channel === null — remove it here so it cannot leak.
      if (cancelled) {
        supabase.removeChannel(channel);
        channel = null;
      }
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [table, enabled]);
}
